import { sensitiveValuesModeKey } from '@/components/overview/sub-components/sensitive-values-switch/SensitiveValuesSwitch.vue';
import {
  CompensationForm,
  ContractorType,
  ProjectType,
} from 'cesg-shared/dist/models/schemas/domain';
import {
  computed,
  inject,
  reactive,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue';
import type {
  ProjectMetaData,
  ProjectMetaDataMode,
  UseProjectMetaDataLogicProps,
} from '../types';

const toEnum = <T extends Record<string, string>>(
  value: string,
  enumObj: T,
): T[keyof T] | '' =>
  Object.values(enumObj).includes(value) ? (value as T[keyof T]) : '';

const createEventHandler = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Events extends Record<string, (...args: any[]) => void>,
>(
  events: Events,
) => events;

export const useProjectMetaDataLogic = (
  props: MaybeRefOrGetter<UseProjectMetaDataLogicProps>,
  emit: (event: 'save', data: ProjectMetaData) => void,
) => {
  const sensitiveMode = inject(sensitiveValuesModeKey);
  const mode = ref<ProjectMetaDataMode>(toValue(props).mode ?? 'normal');

  const metaData = reactive<ProjectMetaData>({
    ...toValue(props).data,
    contractorType: toEnum(toValue(props).data.contractorType, ContractorType),
    compensationForm: toEnum(
      toValue(props).data.compensationForm,
      CompensationForm,
    ),
    type: toEnum(toValue(props).data.type, ProjectType),
  });

  let snapshot: ProjectMetaData | null = null;

  const intFields: (keyof ProjectMetaData)[] = ['size', 'ataSum'];

  const floatFields: (keyof ProjectMetaData)[] = ['arvode'];

  const enumFields = [
    ['contractorType', ContractorType],
    ['compensationForm', CompensationForm],
    ['type', ProjectType],
  ] as const satisfies readonly [
    keyof ProjectMetaData,
    Record<string, string>,
  ][];

  // Data watcher before mode watcher (Vue flushes in creation order)
  watch(
    () => toValue(props).data,
    (newData) => {
      if (mode.value === 'normal' || mode.value === 'read-only') {
        Object.assign(metaData, {
          ...newData,
          contractorType: toEnum(newData.contractorType, ContractorType),
          compensationForm: toEnum(newData.compensationForm, CompensationForm),
          type: toEnum(newData.type, ProjectType),
        });
      }
    },
    { deep: true },
  );

  watch(
    () => toValue(props).isSaving,
    (saving) => {
      if (!saving && mode.value === 'saving') {
        mode.value = 'normal';
      }
    },
  );

  const action = createEventHandler({
    toggleEdit: () => {
      if (mode.value === 'normal') {
        snapshot = { ...metaData };
        mode.value = 'edit';
      } else if (mode.value === 'edit') {
        snapshot = null;
        emit('save', { ...metaData });
        mode.value = 'saving';
      }
    },

    cancel: () => {
      if (snapshot) {
        Object.assign(metaData, snapshot);
      }
      snapshot = null;
      mode.value = 'normal';
    },

    updateField: (event: { field: keyof ProjectMetaData; value: string }) => {
      const { field, value } = event;
      const enumEntry = enumFields.find(([f]) => f === field);
      if (enumEntry) {
        (metaData as Record<string, unknown>)[field] = toEnum(
          value,
          enumEntry[1],
        );
      } else if (intFields.includes(field)) {
        const parsed = parseInt(value.replace(/\D/g, ''), 10);
        if (!isNaN(parsed))
          (metaData as Record<string, unknown>)[field] = parsed;
      } else if (floatFields.includes(field)) {
        const normalized = value.replace(/\s/g, '').replace(',', '.');
        if (normalized === '') {
          (metaData as Record<string, unknown>)[field] = null;
        } else {
          const parsed = parseFloat(normalized);
          if (!isNaN(parsed))
            (metaData as Record<string, unknown>)[field] = parsed;
        }
      } else {
        (metaData as Record<string, unknown>)[field] = value;
      }
    },
  });

  return {
    state: reactive({
      metaData,
      resultHidden: computed(
        () =>
          sensitiveMode?.value === 'presentation' &&
          metaData.compensationForm === 'Fast',
      ),
      resultBlurred: computed(() => metaData.compensationForm === 'Fast'),
      arvodeVisible: computed(
        () => metaData.compensationForm === CompensationForm.LOPANDE,
      ),
      mode,
      get hasChanges() {
        return (
          snapshot !== null &&
          JSON.stringify(metaData) !== JSON.stringify(snapshot)
        );
      },
      get contractorSum() {
        return metaData.undertakingSum ?? null;
      },
      get percentageAta() {
        return metaData.contractSum
          ? (metaData.ataSum / metaData.contractSum) * 100
          : 0;
      },
    }),
    action,
  };
};
